import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MdEdit, MdDelete, MdPeople, MdTrendingUp } from 'react-icons/md';
import { FiPlus, FiPackage, FiRefreshCw, FiUsers, FiDollarSign } from 'react-icons/fi';
import Layout from '../components/Layout';
import Modal from '../components/Modal';
import Toast, { ToastType } from '../components/Toast';
import ConfirmDialog from '../components/ConfirmDialog';
import { apiService, getMediaUrl } from '../services/api';
import { Package } from '../types';

const Packages: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [packages, setPackages] = useState<Package[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [viewPackage, setViewPackage] = useState<Package | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<Package | null>(null);
  const [stats, setStats] = useState<any>(null);
  const [formData, setFormData] = useState({
    title: '',
    fr_title: '',
    description: '',
    fr_description: '',
    data_retention: '',
    monthly_price: '',
    monthly_discount_price: '',
    yearly_price: '',
    yearly_discount_price: '',
    discount_validity: '',
    features: [] as string[],
    fr_features: [] as string[],
    storage_mo: '',
    total_user: '',
    monthly_limit_transaction_ai: '',
    total_transaction_ai: '',
    max_upload: '0',
    max_jobs: '0',
    is_enabled: true,
  });
  const [featureInput, setFeatureInput] = useState('');
  const [frFeatureInput, setFrFeatureInput] = useState('');
  const [pictureFile, setPictureFile] = useState<File | null>(null);
  const [picturePreview, setPicturePreview] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'danger' | 'warning' | 'success';
    onConfirm: () => void;
  }>({ isOpen: false, title: '', message: '', type: 'danger', onConfirm: () => {} });

  const fetchPackages = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiService.getPackages();
      if (response.success) {
        setPackages(response.data);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
      setToast({ message: 'Failed to load packages. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await apiService.getPackageStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching package stats:', error);
    }
  }, []);

  useEffect(() => {
    fetchPackages();
    fetchStats();
  }, [location.pathname, fetchPackages, fetchStats]); // Re-fetch when navigating to this page

  const resetForm = () => {
    setFormData({
      title: '',
      fr_title: '',
      description: '',
      fr_description: '',
      data_retention: '',
      monthly_price: '',
      monthly_discount_price: '',
      yearly_price: '',
      yearly_discount_price: '',
      discount_validity: '',
      features: [],
      fr_features: [],
      storage_mo: '',
      total_user: '',
      monthly_limit_transaction_ai: '',
      total_transaction_ai: '',
      max_upload: '0',
      max_jobs: '0',
      is_enabled: true,
    });
    setPictureFile(null);
    setPicturePreview(null);
    setEditMode(false);
    setSelectedPackage(null);
    setFeatureInput('');
    setFrFeatureInput('');
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (pkg: Package) => {
    setSelectedPackage(pkg);
    setFormData({
      title: pkg.title,
      fr_title: pkg.fr_title || '',
      description: pkg.description,
      fr_description: pkg.fr_description || '',
      data_retention: pkg.data_retention?.toString() || '',
      monthly_price: pkg.monthly_price,
      monthly_discount_price: pkg.monthly_discount_price || '',
      yearly_price: pkg.yearly_price,
      yearly_discount_price: pkg.yearly_discount_price || '',
      discount_validity: pkg.discount_validity || '',
      features: Array.isArray(pkg.features) ? pkg.features : [],
      fr_features: Array.isArray(pkg.fr_features) ? pkg.fr_features : [],
      storage_mo: pkg.storage_mo.toString(),
      total_user: pkg.total_user.toString(),
      monthly_limit_transaction_ai: pkg.monthly_limit_transaction_ai.toString(),
      total_transaction_ai: pkg.total_transaction_ai.toString(),
      max_upload: pkg.max_upload?.toString() || '0',
      max_jobs: pkg.max_jobs?.toString() || '0',
      is_enabled: pkg.is_enabled,
    });
    setPicturePreview(getMediaUrl(pkg.picture));
    setPictureFile(null);
    setEditMode(true);
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Always use FormData to support file uploads
      const formDataToSend = new FormData();
      
      // Add all text/number fields
      formDataToSend.append('title', formData.title);
      formDataToSend.append('fr_title', formData.fr_title || '');
      formDataToSend.append('description', formData.description);
      formDataToSend.append('fr_description', formData.fr_description || '');
      formDataToSend.append('monthly_price', formData.monthly_price);
      formDataToSend.append('yearly_price', formData.yearly_price);
      formDataToSend.append('storage_mo', formData.storage_mo);
      formDataToSend.append('total_user', formData.total_user);
      formDataToSend.append('monthly_limit_transaction_ai', formData.monthly_limit_transaction_ai);
      formDataToSend.append('total_transaction_ai', formData.total_transaction_ai);
      formDataToSend.append('max_upload', formData.max_upload || '0');
      formDataToSend.append('max_jobs', formData.max_jobs || '0');
      formDataToSend.append('is_enabled', formData.is_enabled.toString());
      
      // Add optional fields
      if (formData.monthly_discount_price) {
        formDataToSend.append('monthly_discount_price', formData.monthly_discount_price);
      }
      if (formData.yearly_discount_price) {
        formDataToSend.append('yearly_discount_price', formData.yearly_discount_price);
      }
      if (formData.discount_validity) {
        formDataToSend.append('discount_validity', formData.discount_validity);
      }
      if (formData.data_retention) {
        // Convert to integer before sending
        const retentionValue = parseInt(formData.data_retention, 10);
        if (!isNaN(retentionValue)) {
          formDataToSend.append('data_retention', retentionValue.toString());
        }
      }
      
      // Always add features arrays as JSON (even if empty)
      formDataToSend.append('features', JSON.stringify(formData.features || []));
      formDataToSend.append('fr_features', JSON.stringify(formData.fr_features || []));
      
      // Add picture if selected
      if (pictureFile) {
        formDataToSend.append('picture', pictureFile);
      }

      console.log('📦 Submitting package data:', {
        editMode,
        packageId: selectedPackage?.id,
        hasNewPicture: !!pictureFile,
        features: formData.features,
        featuresCount: formData.features?.length || 0,
        fr_features: formData.fr_features,
        frFeaturesCount: formData.fr_features?.length || 0,
        featuresJSON: JSON.stringify(formData.features || []),
        frFeaturesJSON: JSON.stringify(formData.fr_features || []),
        discountValidity: formData.discount_validity,
        dataRetention: formData.data_retention
      });

      if (editMode && selectedPackage) {
        await apiService.updatePackage(selectedPackage.id, formDataToSend);
        setToast({ message: 'Package updated successfully!', type: 'success' });
      } else {
        await apiService.createPackage(formDataToSend);
        setToast({ message: 'Package created successfully!', type: 'success' });
      }

      setShowModal(false);
      resetForm();
      fetchPackages();
    } catch (error: any) {
      console.error('Error saving package:', error);
      setToast({ message: error.message || 'Failed to save package.', type: 'error' });
    }
  };

  const handleToggleStatus = async (pkg: Package) => {
    const action = pkg.is_enabled ? 'disable' : 'enable';
    try {
      await apiService.togglePackageStatus(pkg.id);
      setToast({ message: `Package ${action}d successfully!`, type: 'success' });
      fetchPackages();
    } catch (error: any) {
      console.error('Error toggling package status:', error);
      setToast({ message: error.message || `Failed to ${action} package.`, type: 'error' });
    }
  };

  const handleDelete = (pkg: Package) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Package',
      message: `Are you sure you want to delete the package "${pkg.title}"? This action cannot be undone.`,
      type: 'danger',
      onConfirm: async () => {
        try {
          await apiService.deletePackage(pkg.id);
          setToast({ message: 'Package deleted successfully!', type: 'success' });
          fetchPackages();
        } catch (error: any) {
          console.error('Error deleting package:', error);
          setToast({ message: error.message || 'Failed to delete package.', type: 'error' });
        }
      },
    });
  };

  const formatPrice = (price: string | null) => {
    if (!price) return '-';
    return `€${parseFloat(price).toFixed(2)}`;
  };

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return '-';
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  return (
    <Layout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-navy-700 dark:text-white">Packages</h1>
            
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/settings/packages/add')}
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-4 py-2 text-white shadow-lg transition hover:bg-brand-600"
            >
              <FiPlus className="h-5 w-5" />
              Add Package
            </button>
          </div>
        </div>

        {/* Subscription Statistics */}
        {stats && (
          <div className="mb-6">
            <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 p-5 shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-100">Total Users</p>
                    <p className="text-3xl font-bold text-white">{stats.overview.total_users}</p>
                  </div>
                  <div className="rounded-full bg-white/20 p-3">
                    <MdPeople className="h-8 w-8 text-white" />
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 p-5 shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-100">Total Packages</p>
                    <p className="text-3xl font-bold text-white">{stats.overview.total_packages}</p>
                    <p className="mt-1 text-xs text-blue-100">{stats.overview.active_packages} active</p>
                  </div>
                  <div className="rounded-full bg-white/20 p-3">
                    <FiPackage className="h-8 w-8 text-white" />
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-gradient-to-br from-green-500 to-green-600 p-5 shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-100">Monthly Revenue</p>
                    <p className="text-3xl font-bold text-white">€{stats.overview.total_monthly_revenue}</p>
                  </div>
                  <div className="rounded-full bg-white/20 p-3">
                    <FiDollarSign className="h-8 w-8 text-white" />
                  </div>
                </div>
              </div>

              <div className="rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 p-5 shadow-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-100">Avg Users/Package</p>
                    <p className="text-3xl font-bold text-white">{stats.overview.avg_users_per_package}</p>
                  </div>
                  <div className="rounded-full bg-white/20 p-3">
                    <MdTrendingUp className="h-8 w-8 text-white" />
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* Packages Table */}
        <div className="rounded-lg bg-white p-6 shadow-md dark:bg-navy-800">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-500 border-t-transparent"></div>
            </div>
          ) : packages.length === 0 ? (
            <div className="py-12 text-center">
              <FiPackage className="mx-auto h-16 w-16 text-gray-400 dark:text-gray-600" />
              <p className="mt-4 text-gray-600 dark:text-gray-400">No packages found.</p>
              <button
                onClick={() => navigate('/settings/packages/add')}
                className="mt-4 rounded-lg bg-brand-500 px-6 py-2 text-white shadow-lg transition hover:bg-brand-600"
              >
                Create First Package
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-navy-700">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Package
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Monthly Price
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Yearly Price
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Storage (MB)
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                      AI Trans/Mo
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Max Upload
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Max Jobs
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Data Retention
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Subscribers
                    </th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {packages.map((pkg) => (
                    <tr
                      key={pkg.id}
                      onClick={() => {
                        setViewPackage(pkg);
                        setShowDetailsModal(true);
                      }}
                      className="cursor-pointer border-b border-gray-100 transition hover:bg-gray-50 dark:border-navy-700 dark:hover:bg-navy-700"
                    >
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          {pkg.picture ? (
                            <img
                              src={getMediaUrl(pkg.picture) || ''}
                              alt={pkg.title}
                              className="h-12 w-12 rounded-lg object-cover border border-gray-200 dark:border-navy-600"
                            />
                          ) : (
                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-brand-100 dark:bg-brand-900/20">
                              <FiPackage className="h-6 w-6 text-brand-500 dark:text-brand-400" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium text-navy-700 dark:text-white">{pkg.title}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-1">
                              {pkg.description}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-medium text-navy-700 dark:text-white">
                            {formatPrice(pkg.monthly_price)}
                          </p>
                          {pkg.monthly_discount_price && (
                            <p className="text-sm text-green-600 dark:text-green-400">
                              {formatPrice(pkg.monthly_discount_price)}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div>
                          <p className="font-medium text-navy-700 dark:text-white">
                            {formatPrice(pkg.yearly_price)}
                          </p>
                          {pkg.yearly_discount_price && (
                            <p className="text-sm text-green-600 dark:text-green-400">
                              {formatPrice(pkg.yearly_discount_price)}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 text-navy-700 dark:text-white">
                        {pkg.storage_mo.toLocaleString()}
                      </td>
                      <td className="px-4 py-4 text-navy-700 dark:text-white">
                        {pkg.monthly_limit_transaction_ai.toLocaleString()}
                      </td>
                      <td className="px-4 py-4 text-navy-700 dark:text-white">
                        <span className="inline-flex items-center gap-1">
                          <span className="font-medium">{pkg.max_upload || 0}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{pkg.max_upload === 0 ? '∞' : 'files'}</span>
                        </span>
                      </td>
                      <td className="px-4 py-4 text-navy-700 dark:text-white">
                        <span className="inline-flex items-center gap-1">
                          <span className="font-medium">{pkg.max_jobs || 0}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{pkg.max_jobs === 0 ? '∞' : 'jobs'}</span>
                        </span>
                      </td>
                      <td className="px-4 py-4 text-navy-700 dark:text-white">
                        {pkg.data_retention ? (
                          <span className="inline-flex items-center gap-1">
                            <span className="font-medium">{pkg.data_retention}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">days</span>
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-600">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2">
                          <FiUsers className="h-4 w-4 text-brand-500 dark:text-brand-400" />
                          <span className="font-medium text-navy-700 dark:text-white">
                            {pkg.user_count || 0}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/settings/packages/edit/${pkg.id}`);
                            }}
                            className="rounded-lg p-2 text-blue-600 transition hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                            title="Edit Package"
                          >
                            <MdEdit className="h-5 w-5" />
                          </button>
                          {/* Toggle Switch */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleStatus(pkg);
                            }}
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                              pkg.is_enabled ? 'bg-green-500' : 'bg-gray-300'
                            }`}
                            title={pkg.is_enabled ? 'Active - Click to disable' : 'Disabled - Click to enable'}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                pkg.is_enabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(pkg);
                            }}
                            className="rounded-lg p-2 text-red-600 transition hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                            title="Delete Package"
                          >
                            <MdDelete className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={editMode ? '✏️ Edit Package' : '✨ Create New Package'}
        size="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 border-b border-gray-200 pb-2 dark:border-navy-600">
              <FiPackage className="h-5 w-5 text-brand-500" />
              <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                Basic Information
              </h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  🇬🇧 Package Name (English) *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Starter, Professional, Enterprise"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  🇫🇷 Package Name (French)
                  <span className="ml-1 text-xs text-gray-500">(optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.fr_title}
                  onChange={(e) => setFormData({ ...formData, fr_title: e.target.value })}
                  placeholder="e.g., Débutant, Professionnel, Entreprise"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  🇬🇧 Description (English) *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Describe the key features and benefits..."
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                  required
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  🇫🇷 Description (French)
                  <span className="ml-1 text-xs text-gray-500">(optional)</span>
                </label>
                <textarea
                  value={formData.fr_description}
                  onChange={(e) => setFormData({ ...formData, fr_description: e.target.value })}
                  rows={3}
                  placeholder="Décrivez les principales fonctionnalités..."
                  className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Descriptions will be displayed to users when choosing a package
            </p>

            {/* Picture Upload */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                📷 Package Picture
                <span className="ml-1 text-xs text-gray-500">(optional)</span>
              </label>
              <div className="flex items-center gap-4">
                {picturePreview && (
                  <div className="flex-shrink-0">
                    <img
                      src={picturePreview}
                      alt="Package preview"
                      className="h-20 w-20 rounded-lg object-cover border-2 border-gray-200 dark:border-navy-600"
                    />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        setPictureFile(file);
                        setPicturePreview(URL.createObjectURL(file));
                      }
                    }}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Upload an icon or image for this package (PNG, JPG, max 2MB)
                  </p>
                </div>
              </div>
            </div>

            {/* Data Retention */}
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                📅 Data Retention (Days)
                <span className="ml-1 text-xs text-gray-500">(optional)</span>
              </label>
              <input
                type="number"
                min="0"
                value={formData.data_retention}
                onChange={(e) => setFormData({ ...formData, data_retention: e.target.value })}
                placeholder="e.g., 30, 90, 365"
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Number of days to retain user data for this package
              </p>
            </div>

            {/* Features Section */}
            <div className="grid grid-cols-2 gap-4">
              {/* English Features */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  🇬🇧 Package Features (English)
                  <span className="ml-1 text-xs text-gray-500">(optional)</span>
                </label>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={featureInput}
                      onChange={(e) => setFeatureInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (featureInput.trim()) {
                            setFormData({ 
                              ...formData, 
                              features: [...formData.features, featureInput.trim()] 
                            });
                            setFeatureInput('');
                          }
                        }
                      }}
                      placeholder="e.g., 24/7 Premium Support"
                      className="flex-1 rounded-lg border border-gray-300 px-4 py-2 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (featureInput.trim()) {
                          setFormData({ 
                            ...formData, 
                            features: [...formData.features, featureInput.trim()] 
                          });
                          setFeatureInput('');
                        }
                      }}
                      className="rounded-lg bg-brand-500 px-4 py-2 text-white transition hover:bg-brand-600"
                    >
                      Add
                    </button>
                  </div>
                  
                  {formData.features.length > 0 && (
                    <div className="space-y-2">
                      {formData.features.map((feature, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-navy-600 dark:bg-navy-700"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-brand-500 dark:text-brand-400">✓</span>
                            <span className="text-sm text-navy-700 dark:text-white">{feature}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                features: formData.features.filter((_, i) => i !== index)
                              });
                            }}
                            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* French Features */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  🇫🇷 Package Features (French)
                  <span className="ml-1 text-xs text-gray-500">(optional)</span>
                </label>
                <div className="space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={frFeatureInput}
                      onChange={(e) => setFrFeatureInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (frFeatureInput.trim()) {
                            setFormData({ 
                              ...formData, 
                              fr_features: [...formData.fr_features, frFeatureInput.trim()] 
                            });
                            setFrFeatureInput('');
                          }
                        }
                      }}
                      placeholder="e.g., Support Premium 24/7"
                      className="flex-1 rounded-lg border border-gray-300 px-4 py-2 focus:border-brand-500 focus:ring-2 focus:ring-brand-200 dark:border-navy-600 dark:bg-navy-700 dark:text-white dark:focus:border-brand-400"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (frFeatureInput.trim()) {
                          setFormData({ 
                            ...formData, 
                            fr_features: [...formData.fr_features, frFeatureInput.trim()] 
                          });
                          setFrFeatureInput('');
                        }
                      }}
                      className="rounded-lg bg-brand-500 px-4 py-2 text-white transition hover:bg-brand-600"
                    >
                      Add
                    </button>
                  </div>
                  
                  {formData.fr_features.length > 0 && (
                    <div className="space-y-2">
                      {formData.fr_features.map((feature, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-navy-600 dark:bg-navy-700"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-brand-500 dark:text-brand-400">✓</span>
                            <span className="text-sm text-navy-700 dark:text-white">{feature}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                fr_features: formData.fr_features.filter((_, i) => i !== index)
                              });
                            }}
                            className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Add key features in both languages (e.g., "Priority Support" / "Support Prioritaire")
            </p>
          </div>

          {/* Pricing Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 border-b border-gray-200 pb-2 dark:border-navy-600">
              <span className="text-lg">💰</span>
              <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                Pricing Plans
              </h3>
            </div>

            {/* Monthly Pricing */}
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 dark:border-blue-900/30 dark:bg-blue-900/10">
              <div className="mb-3 flex items-center gap-2">
                <span className="text-sm font-semibold text-blue-700 dark:text-blue-400">
                  📅 Monthly Subscription
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Regular Price * ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.monthly_price}
                    onChange={(e) => setFormData({ ...formData, monthly_price: e.target.value })}
                    placeholder="29.99"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Discount Price ($)
                    <span className="ml-1 text-xs text-gray-500">(optional)</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.monthly_discount_price}
                    onChange={(e) => setFormData({ ...formData, monthly_discount_price: e.target.value })}
                    placeholder="24.99"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Yearly Pricing */}
            <div className="rounded-lg border border-purple-200 bg-purple-50 p-4 dark:border-purple-900/30 dark:bg-purple-900/10">
              <div className="mb-3 flex items-center gap-2">
                <span className="text-sm font-semibold text-purple-700 dark:text-purple-400">
                  📆 Yearly Subscription
                </span>
                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  Best Value
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Regular Price * ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.yearly_price}
                    onChange={(e) => setFormData({ ...formData, yearly_price: e.target.value })}
                    placeholder="299.99"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Discount Price ($)
                    <span className="ml-1 text-xs text-gray-500">(optional)</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.yearly_discount_price}
                    onChange={(e) => setFormData({ ...formData, yearly_discount_price: e.target.value })}
                    placeholder="249.99"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  />
                </div>
              </div>
            </div>

            {/* Discount Validity */}
            {(formData.monthly_discount_price || formData.yearly_discount_price) && (
              <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 dark:border-orange-900/30 dark:bg-orange-900/10">
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  ⏰ Discount Valid Until
                  <span className="ml-1 text-xs text-gray-500">(optional)</span>
                </label>
                <input
                  type="date"
                  value={formData.discount_validity}
                  onChange={(e) => setFormData({ ...formData, discount_validity: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                />
                <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                  Leave empty for permanent discount, or set an expiry date
                </p>
              </div>
            )}
          </div>

          {/* Package Limits Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 border-b border-gray-200 pb-2 dark:border-navy-600">
              <span className="text-lg">⚡</span>
              <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                Package Limits & Features
              </h3>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  💾 Storage Space (MB) *
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.storage_mo}
                  onChange={(e) => setFormData({ ...formData, storage_mo: e.target.value })}
                  placeholder="1024"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">1024 MB = 1 GB</p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  👥 Max Team Members *
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.total_user}
                  onChange={(e) => setFormData({ ...formData, total_user: e.target.value })}
                  placeholder="5"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Number of users allowed</p>
              </div>
            </div>

            <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-4 dark:border-indigo-900/30 dark:bg-indigo-900/10">
              <div className="mb-3 flex items-center gap-2">
                <span className="text-sm font-semibold text-indigo-700 dark:text-indigo-400">
                  🤖 AI Analysis Limits
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Monthly AI Analysis *
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.monthly_limit_transaction_ai}
                    onChange={(e) => setFormData({ ...formData, monthly_limit_transaction_ai: e.target.value })}
                    placeholder="100"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">CVs per month</p>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Total AI Analysis *
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.total_transaction_ai}
                    onChange={(e) => setFormData({ ...formData, total_transaction_ai: e.target.value })}
                    placeholder="1000"
                    className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">Lifetime limit</p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {/* Max Upload Limit */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  📤 Maximum Files Upload *
                </label>
                <input
                  type="number"
                  min="0"
                  max="1000"
                  value={formData.max_upload}
                  onChange={(e) => setFormData({ ...formData, max_upload: e.target.value })}
                  placeholder="0 = unlimited"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Files per upload. 0 = unlimited</p>
              </div>

              {/* Max Jobs Limit */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  💼 Maximum Jobs *
                </label>
                <input
                  type="number"
                  min="0"
                  max="1000"
                  value={formData.max_jobs}
                  onChange={(e) => setFormData({ ...formData, max_jobs: e.target.value })}
                  placeholder="0 = unlimited"
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Number of jobs allowed. 0 = unlimited</p>
              </div>
            </div>
          </div>

          {/* Status Section */}
          <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 p-4 dark:border-navy-600 dark:bg-navy-700/50">
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="is_enabled"
                checked={formData.is_enabled}
                onChange={(e) => setFormData({ ...formData, is_enabled: e.target.checked })}
                className="h-5 w-5 rounded border-gray-300 text-brand-500 focus:ring-brand-500"
              />
              <label htmlFor="is_enabled" className="flex-1 text-sm font-medium text-gray-700 dark:text-gray-300">
                <span className="flex items-center gap-2">
                  {formData.is_enabled ? '✅' : '⭕'} 
                  {formData.is_enabled ? 'Package is Active' : 'Package is Disabled'}
                </span>
                <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                  {formData.is_enabled 
                    ? 'Users can see and purchase this package' 
                    : 'Package is hidden from users'}
                </span>
              </label>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 border-t border-gray-200 pt-4 dark:border-navy-600">
            <button
              type="button"
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
              className="rounded-lg border border-gray-300 px-6 py-2.5 font-medium text-gray-700 transition hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex items-center gap-2 rounded-lg bg-brand-500 px-6 py-2.5 font-medium text-white transition hover:bg-brand-600 shadow-lg"
            >
              {editMode ? '💾 Update Package' : '✨ Create Package'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        onConfirm={() => {
          confirmDialog.onConfirm();
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        }}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
      />

      {/* Package Details Modal */}
      {viewPackage && (
        <Modal
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setViewPackage(null);
          }}
          title={`📦 Package Details`}
          size="2xl"
        >
          <div className="max-h-[80vh] space-y-5 overflow-y-auto pr-2">
            {/* Header Section with Image & Key Info */}
            <div className="rounded-xl border-2 border-brand-200 bg-gradient-to-br from-brand-50 to-blue-50 p-5 dark:border-brand-700 dark:from-brand-900/20 dark:to-blue-900/20">
              <div className="flex items-start gap-5">
                {viewPackage.picture ? (
                  <img
                    src={getMediaUrl(viewPackage.picture) || ''}
                    alt={viewPackage.title}
                    className="h-28 w-28 flex-shrink-0 rounded-2xl object-cover border-4 border-white shadow-lg dark:border-navy-600"
                  />
                ) : (
                  <div className="flex h-28 w-28 flex-shrink-0 items-center justify-center rounded-2xl border-4 border-white bg-brand-100 shadow-lg dark:border-navy-600 dark:bg-brand-900/20">
                    <FiPackage className="h-14 w-14 text-brand-500 dark:text-brand-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-2xl font-bold text-navy-700 dark:text-white">
                        {viewPackage.title}
                      </h3>
                      {viewPackage.fr_title && (
                        <p className="mt-1 text-lg font-medium text-gray-600 dark:text-gray-400">
                          🇫🇷 {viewPackage.fr_title}
                        </p>
                      )}
                    </div>
                    <div className="flex flex-col gap-2">
                      <span
                        className={`inline-flex items-center justify-center rounded-full px-4 py-1.5 text-sm font-bold ${
                          viewPackage.is_enabled
                            ? 'bg-green-500 text-white shadow-md'
                            : 'bg-red-500 text-white shadow-md'
                        }`}
                      >
                        {viewPackage.is_enabled ? '✓ Active' : '✗ Disabled'}
                      </span>
                      {viewPackage.has_active_discount && (
                        <span className="inline-flex items-center justify-center rounded-full bg-orange-500 px-4 py-1.5 text-sm font-bold text-white shadow-md">
                          🏷️ Discount
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="rounded-lg bg-white/70 p-3 dark:bg-navy-800/70">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">🇬🇧 ENGLISH DESCRIPTION</p>
                      <p className="mt-1 text-sm leading-relaxed text-navy-700 dark:text-white">{viewPackage.description}</p>
                    </div>
                    {viewPackage.fr_description && (
                      <div className="rounded-lg bg-white/70 p-3 dark:bg-navy-800/70">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">🇫🇷 FRENCH DESCRIPTION</p>
                        <p className="mt-1 text-sm leading-relaxed text-navy-700 dark:text-white">{viewPackage.fr_description}</p>
                      </div>
                    )}
                  </div>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="inline-flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-white shadow-md">
                      <FiUsers className="h-4 w-4" />
                      <span className="text-sm font-bold">{viewPackage.user_count || 0}</span>
                      <span className="text-xs">Subscribers</span>
                    </div>
                    <div className="inline-flex items-center gap-2 rounded-lg bg-purple-500 px-4 py-2 text-white shadow-md">
                      <span className="text-sm font-bold">ID: {viewPackage.id}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing Section */}
            <div className="rounded-xl border-2 border-blue-200 bg-blue-50 p-5 dark:border-blue-900/30 dark:bg-blue-900/10">
              <div className="mb-4 flex items-center gap-2">
                <span className="text-2xl">💰</span>
                <h4 className="text-lg font-bold text-blue-800 dark:text-blue-300">Pricing Plans</h4>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {/* Monthly Pricing */}
                <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-navy-800">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="rounded bg-blue-100 px-2 py-1 text-xs font-bold text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                      📅 MONTHLY
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <p className="text-3xl font-bold text-navy-700 dark:text-white">{formatPrice(viewPackage.monthly_price)}</p>
                    <p className="text-sm text-gray-500">/month</p>
                  </div>
                  {viewPackage.monthly_discount_price && (
                    <div className="mt-2 rounded bg-green-100 px-3 py-2 dark:bg-green-900/20">
                      <p className="text-xs font-semibold text-green-700 dark:text-green-400">💵 DISCOUNTED PRICE</p>
                      <p className="text-xl font-bold text-green-600 dark:text-green-400">
                        {formatPrice(viewPackage.monthly_discount_price)}
                      </p>
                      <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                        Save: {formatPrice((parseFloat(viewPackage.monthly_price) - parseFloat(viewPackage.monthly_discount_price || '0')).toString())}
                      </p>
                    </div>
                  )}
                  <div className="mt-3 text-center">
                    <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Current Price</p>
                    <p className="text-lg font-bold text-brand-500 dark:text-brand-400">{formatPrice(viewPackage.current_monthly_price)}</p>
                  </div>
                </div>

                {/* Yearly Pricing */}
                <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-navy-800">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="rounded bg-purple-100 px-2 py-1 text-xs font-bold text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                      📆 YEARLY
                    </span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <p className="text-3xl font-bold text-navy-700 dark:text-white">{formatPrice(viewPackage.yearly_price)}</p>
                    <p className="text-sm text-gray-500">/year</p>
                  </div>
                  {viewPackage.yearly_discount_price && (
                    <div className="mt-2 rounded bg-green-100 px-3 py-2 dark:bg-green-900/20">
                      <p className="text-xs font-semibold text-green-700 dark:text-green-400">💵 DISCOUNTED PRICE</p>
                      <p className="text-xl font-bold text-green-600 dark:text-green-400">
                        {formatPrice(viewPackage.yearly_discount_price)}
                      </p>
                      <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                        Save: {formatPrice((parseFloat(viewPackage.yearly_price) - parseFloat(viewPackage.yearly_discount_price || '0')).toString())}
                      </p>
                    </div>
                  )}
                  <div className="mt-3 text-center">
                    <p className="text-sm font-semibold text-gray-600 dark:text-gray-400">Current Price</p>
                    <p className="text-lg font-bold text-brand-500 dark:text-brand-400">{formatPrice(viewPackage.current_yearly_price)}</p>
                  </div>
                </div>
              </div>
              {viewPackage.discount_validity && (
                <div className="mt-4 rounded-lg bg-orange-100 px-4 py-3 dark:bg-orange-900/20">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">⏰</span>
                    <div>
                      <p className="text-xs font-bold text-orange-800 dark:text-orange-300">DISCOUNT VALID UNTIL</p>
                      <p className="text-sm font-semibold text-orange-700 dark:text-orange-400">{formatDate(viewPackage.discount_validity)}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Resource Limits Section */}
            <div className="rounded-xl border-2 border-purple-200 bg-purple-50 p-5 dark:border-purple-900/30 dark:bg-purple-900/10">
              <div className="mb-4 flex items-center gap-2">
                <span className="text-2xl">⚡</span>
                <h4 className="text-lg font-bold text-purple-800 dark:text-purple-300">Resource Limits & Quotas</h4>
              </div>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                {/* Storage */}
                <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-navy-800">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">💾</span>
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400">STORAGE</p>
                  </div>
                  <p className="mt-2 text-2xl font-bold text-navy-700 dark:text-white">{viewPackage.storage_mo.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">MB</p>
                </div>

                {/* Max Users */}
                <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-navy-800">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">👥</span>
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400">MAX USERS</p>
                  </div>
                  <p className="mt-2 text-2xl font-bold text-navy-700 dark:text-white">{viewPackage.total_user}</p>
                  <p className="text-xs text-gray-500">users</p>
                </div>

                {/* Monthly AI */}
                <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-navy-800">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🤖</span>
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400">MONTHLY AI</p>
                  </div>
                  <p className="mt-2 text-2xl font-bold text-navy-700 dark:text-white">{viewPackage.monthly_limit_transaction_ai.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">transactions</p>
                </div>

                {/* Total AI */}
                <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-navy-800">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">🔮</span>
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400">TOTAL AI</p>
                  </div>
                  <p className="mt-2 text-2xl font-bold text-navy-700 dark:text-white">{viewPackage.total_transaction_ai.toLocaleString()}</p>
                  <p className="text-xs text-gray-500">lifetime</p>
                </div>

                {/* Max Upload */}
                <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-navy-800">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">📤</span>
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400">MAX UPLOAD</p>
                  </div>
                  <p className="mt-2 text-2xl font-bold text-navy-700 dark:text-white">
                    {viewPackage.max_upload === 0 ? '∞' : viewPackage.max_upload}
                  </p>
                  <p className="text-xs text-gray-500">{viewPackage.max_upload === 0 ? 'unlimited' : 'files per upload'}</p>
                </div>

                {/* Max Jobs */}
                <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-navy-800">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">💼</span>
                    <p className="text-xs font-bold text-gray-500 dark:text-gray-400">MAX JOBS</p>
                  </div>
                  <p className="mt-2 text-2xl font-bold text-navy-700 dark:text-white">
                    {viewPackage.max_jobs === 0 ? '∞' : viewPackage.max_jobs}
                  </p>
                  <p className="text-xs text-gray-500">{viewPackage.max_jobs === 0 ? 'unlimited' : 'jobs allowed'}</p>
                </div>
              </div>
              {viewPackage.data_retention && (
                <div className="mt-4 rounded-lg bg-indigo-100 px-4 py-3 dark:bg-indigo-900/20">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">📅</span>
                    <div>
                      <p className="text-xs font-bold text-indigo-800 dark:text-indigo-300">DATA RETENTION PERIOD</p>
                      <p className="text-sm font-semibold text-indigo-700 dark:text-indigo-400">{viewPackage.data_retention} days</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Features Section */}
            {(viewPackage.features && viewPackage.features.length > 0) || (viewPackage.fr_features && viewPackage.fr_features.length > 0) ? (
              <div className="rounded-xl border-2 border-green-200 bg-green-50 p-5 dark:border-green-900/30 dark:bg-green-900/10">
                <div className="mb-4 flex items-center gap-2">
                  <span className="text-2xl">✨</span>
                  <h4 className="text-lg font-bold text-green-800 dark:text-green-300">Package Features</h4>
                </div>
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                  {viewPackage.features && viewPackage.features.length > 0 && (
                    <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-navy-800">
                      <div className="mb-3 flex items-center gap-2">
                        <span className="rounded bg-green-100 px-2 py-1 text-xs font-bold text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          🇬🇧 ENGLISH
                        </span>
                        <span className="text-xs text-gray-500">{viewPackage.features.length} features</span>
                      </div>
                      <div className="space-y-2.5">
                        {viewPackage.features.map((feature, index) => (
                          <div key={index} className="flex items-start gap-3 rounded-lg bg-green-50 px-3 py-2 dark:bg-green-900/10">
                            <span className="mt-0.5 flex-shrink-0 text-lg text-green-600 dark:text-green-400">✓</span>
                            <span className="text-sm font-medium text-navy-700 dark:text-white">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {viewPackage.fr_features && viewPackage.fr_features.length > 0 && (
                    <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-navy-800">
                      <div className="mb-3 flex items-center gap-2">
                        <span className="rounded bg-blue-100 px-2 py-1 text-xs font-bold text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                          🇫🇷 FRENCH
                        </span>
                        <span className="text-xs text-gray-500">{viewPackage.fr_features.length} features</span>
                      </div>
                      <div className="space-y-2.5">
                        {viewPackage.fr_features.map((feature, index) => (
                          <div key={index} className="flex items-start gap-3 rounded-lg bg-blue-50 px-3 py-2 dark:bg-blue-900/10">
                            <span className="mt-0.5 flex-shrink-0 text-lg text-blue-600 dark:text-blue-400">✓</span>
                            <span className="text-sm font-medium text-navy-700 dark:text-white">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : null}

            {/* Metadata Section */}
            <div className="rounded-xl bg-gray-100 px-5 py-4 dark:bg-navy-800">
              <div className="mb-3 flex items-center gap-2">
                <span className="text-xl">📊</span>
                <h4 className="text-sm font-bold text-gray-700 dark:text-gray-300">Metadata</h4>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">CREATED</p>
                  <p className="mt-1 font-mono text-sm font-medium text-navy-700 dark:text-white">{formatDate(viewPackage.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400">LAST UPDATED</p>
                  <p className="mt-1 font-mono text-sm font-medium text-navy-700 dark:text-white">{formatDate(viewPackage.updated_at)}</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="sticky bottom-0 flex justify-end gap-3 border-t-2 border-gray-200 bg-white pt-4 dark:border-navy-600 dark:bg-navy-900">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="rounded-lg border-2 border-gray-300 px-5 py-2.5 font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700"
              >
                Close
              </button>
              <button
                onClick={() => {
                  if (viewPackage) {
                    navigate(`/settings/packages/edit/${viewPackage.id}`);
                  }
                }}
                className="flex items-center gap-2 rounded-lg bg-blue-500 px-5 py-2.5 font-semibold text-white shadow-md transition hover:bg-blue-600"
              >
                <MdEdit className="h-5 w-5" />
                Edit Package
              </button>
            </div>
          </div>
        </Modal>
      )}
    </Layout>
  );
};

export default Packages;
